// Audio-Bytes aus dem Session-Record heraushalten (Audit M9, 2026-09-24).
//
// Bis hierhin lag jeder MP3-Puffer (TTS-Take) und jede STS-Aufnahme
// (Output + Original) IM Session-Record. Jeder Autosave — alle 800 ms nach
// jedem Tastendruck — schrieb damit alle Bytes aller Assets neu: bei 21
// Assets × Blöcke × Takes 20–100 MB pro Save, Main-Thread ruckelt, großes
// Verlustfenster, Quota-Risiko.
//
// Neu: der Session-Record enthält nur Metadaten; die Bytes liegen im
// IDB-Store `take-audio`, ein Eintrag pro Take (Schlüssel siehe unten),
// geschrieben genau einmal. Dieses Modul ist die pure Hälfte: Auftrennen
// vor dem Speichern, Zusammenführen nach dem Laden, Schlüsselmenge für das
// Aufräumen verwaister Einträge. Die IDB-Seite steckt in indexeddb.ts.

import type { Block, SessionState, STSAssetState, STSTake, Take } from '@shared/types';

export type AudioMap = Map<string, ArrayBuffer>;

export const ttsAudioKey = (takeId: string): string => `tts:${takeId}`;
export const stsOutputKey = (takeId: string): string => `sts:${takeId}:out`;
export const stsInputKey = (takeId: string): string => `sts:${takeId}:in`;

const EMPTY = new ArrayBuffer(0);

function hasBytes(b: ArrayBuffer | undefined | null): b is ArrayBuffer {
  return !!b && b.byteLength > 0;
}

// ── Auftrennen ──────────────────────────────────────────────────────────

function stripBlocks(blocks: Block[], audio: AudioMap): Block[] {
  return blocks.map((b) => ({
    ...b,
    takes: b.takes.map((t) => {
      if (hasBytes(t.buffer)) audio.set(ttsAudioKey(t.id), t.buffer);
      // Das gespeicherte Take-Objekt trägt keinen Puffer mehr; der Typ bleibt
      // `Take`, weil mergeSessionAudio ihn vor jedem Lesen wieder befüllt.
      const { buffer: _buffer, ...rest } = t;
      return rest as Take;
    }),
  }));
}

function stripBlockMap(m: Record<number, Block[]>, audio: AudioMap): Record<number, Block[]> {
  const out: Record<number, Block[]> = {};
  for (const [k, v] of Object.entries(m ?? {})) out[Number(k)] = stripBlocks(v, audio);
  return out;
}

function stripSts(m: Record<number, STSAssetState>, audio: AudioMap): Record<number, STSAssetState> {
  const out: Record<number, STSAssetState> = {};
  for (const [k, st] of Object.entries(m ?? {})) {
    out[Number(k)] = {
      ...st,
      takes: st.takes.map((t) => {
        if (hasBytes(t.audioBuffer)) audio.set(stsOutputKey(t.id), t.audioBuffer);
        if (hasBytes(t.inputBuffer)) audio.set(stsInputKey(t.id), t.inputBuffer);
        const { audioBuffer: _a, inputBuffer: _i, ...rest } = t;
        return rest as STSTake;
      }),
    };
  }
  return out;
}

/** Session ohne Audio-Bytes + die Bytes als Map (Schlüssel → Puffer).
 *  Die Eingabe wird nicht verändert. Derselbe Take in `assetBlocks` und
 *  `freeBlocks` (Mode-Wechsel kopiert) landet nur einmal in der Map. */
export function splitSessionAudio(s: SessionState): { session: SessionState; audio: AudioMap } {
  const audio: AudioMap = new Map();
  const session: SessionState = {
    ...s,
    blocks: stripBlocks(s.blocks ?? [], audio),
    assetBlocks: stripBlockMap(s.assetBlocks, audio),
    freeBlocks: Object.fromEntries(
      Object.entries(s.freeBlocks ?? {}).map(([k, modes]) => [
        Number(k),
        { tts: stripBlocks(modes?.tts ?? [], audio), sts: stripBlocks(modes?.sts ?? [], audio) },
      ]),
    ),
    stsAssets: stripSts(s.stsAssets, audio),
  };
  return { session, audio };
}

/** Alle Audio-Schlüssel, die eine Session referenziert — Basis für das
 *  Löschen verwaister Einträge beim Speichern. */
export function collectAudioKeys(s: SessionState): Set<string> {
  return new Set(splitSessionAudio(s).audio.keys());
}

// ── Zusammenführen ──────────────────────────────────────────────────────

function mergeBlocks(blocks: Block[], audio: AudioMap, missing: string[]): Block[] {
  return blocks.map((b) => {
    const takes: Take[] = [];
    let favIdx = b.favIdx;
    b.takes.forEach((t, i) => {
      const buf = hasBytes(t.buffer) ? t.buffer : audio.get(ttsAudioKey(t.id));
      if (!buf) {
        // Take ohne Bytes ist wertlos — raus, Stern nachführen.
        missing.push(ttsAudioKey(t.id));
        if (favIdx === i) favIdx = -1;
        else if (favIdx > i) favIdx--;
        return;
      }
      takes.push({ ...t, buffer: buf });
    });
    return takes.length === b.takes.length && favIdx === b.favIdx && b.takes.every((t) => hasBytes(t.buffer))
      ? b
      : { ...b, takes, favIdx };
  });
}

function mergeSts(st: STSAssetState, audio: AudioMap, missing: string[]): STSAssetState {
  const takes: STSTake[] = [];
  for (const t of st.takes) {
    const out = hasBytes(t.audioBuffer) ? t.audioBuffer : audio.get(stsOutputKey(t.id));
    if (!out) {
      missing.push(stsOutputKey(t.id));
      continue;
    }
    const input = hasBytes(t.inputBuffer) ? t.inputBuffer : audio.get(stsInputKey(t.id));
    if (!input) missing.push(stsInputKey(t.id));
    // Fehlende Original-Aufnahme ist kein Grund, den fertigen Take zu
    // verlieren — nur ein erneutes STS-Processing wäre damit unmöglich.
    takes.push({ ...t, audioBuffer: out, inputBuffer: input ?? EMPTY });
  }
  const alive = new Set(takes.map((t) => t.id));
  return {
    ...st,
    takes,
    // Master-Clips ohne Quell-Take können nicht spielen (Kaskade wie
    // removeSTSTake).
    master: (st.master ?? []).filter((c) => alive.has(c.takeId)),
  };
}

/** Bytes wieder an die Takes hängen. Takes, deren Bytes fehlen, werden
 *  entfernt (mit Stern-/Master-Nachführung) und in `missing` gemeldet. */
export function mergeSessionAudio(
  s: SessionState,
  audio: AudioMap,
): { session: SessionState; missing: string[] } {
  const missing: string[] = [];
  const assetBlocks: Record<number, Block[]> = {};
  for (const [k, v] of Object.entries(s.assetBlocks ?? {})) assetBlocks[Number(k)] = mergeBlocks(v, audio, missing);
  const freeBlocks: SessionState['freeBlocks'] = {};
  for (const [k, modes] of Object.entries(s.freeBlocks ?? {})) {
    freeBlocks[Number(k)] = {
      tts: mergeBlocks(modes?.tts ?? [], audio, missing),
      sts: mergeBlocks(modes?.sts ?? [], audio, missing),
    };
  }
  const stsAssets: Record<number, STSAssetState> = {};
  for (const [k, st] of Object.entries(s.stsAssets ?? {})) stsAssets[Number(k)] = mergeSts(st, audio, missing);
  return {
    session: {
      ...s,
      blocks: assetBlocks[s.currentAssetIdx] ?? mergeBlocks(s.blocks ?? [], audio, missing),
      assetBlocks,
      freeBlocks,
      stsAssets,
    },
    missing: Array.from(new Set(missing)),
  };
}

/** Enthält der gespeicherte Stand noch eingebettete Bytes (Format vor M9)? */
export function hasEmbeddedAudio(s: SessionState): boolean {
  const blockHas = (blocks: Block[] | undefined) =>
    (blocks ?? []).some((b) => b.takes.some((t) => hasBytes(t.buffer)));
  if (blockHas(s.blocks)) return true;
  if (Object.values(s.assetBlocks ?? {}).some(blockHas)) return true;
  if (Object.values(s.freeBlocks ?? {}).some((m) => blockHas(m?.tts) || blockHas(m?.sts))) return true;
  return Object.values(s.stsAssets ?? {}).some((st) =>
    (st.takes ?? []).some((t) => hasBytes(t.audioBuffer) || hasBytes(t.inputBuffer)),
  );
}

/** Welche gespeicherten Schlüssel nicht mehr referenziert werden. */
export function orphanAudioKeys(stored: Iterable<string>, live: Set<string>): string[] {
  const out: string[] = [];
  for (const k of stored) if (!live.has(k)) out.push(k);
  return out;
}
